import React, { useMemo } from 'react';

import LiveDot from 'screens/chatbot/components/live-dot';
import { ListSummary } from 'screens/chatbot/tabs/dispositions/styled-comps';

import { KNOWLEDGE_BASE_BULK_UPLOAD_URL } from '../../utils';

import {
  SearchInput,
  ListContainer,
  ArticleItem,
  ArticleTitle,
  NewButton,
  BulkUploadLink,
  BulkUploadDisabled,
} from './styled-comps';

// Collapse DRAFT + PUBLISHED + ARCHIVED rows into one entry per logical
// article (article_key). The "head" row prefers PUBLISHED, then DRAFT, then
// ARCHIVED — that's what the user clicks into; the versions panel on the
// right shows every version once selected. We also annotate whether a
// PUBLISHED row exists so the list can show a LIVE dot.
function groupByArticleKey(articles) {
  const byKey = new Map();
  articles.forEach(a => {
    const key = a.article_key;
    if (!byKey.has(key)) {
      byKey.set(key, { head: a, hasPublished: false });
    }
    const entry = byKey.get(key);
    if (a.status === 'PUBLISHED') {
      entry.head = a;
      entry.hasPublished = true;
    } else if (a.status === 'DRAFT' && entry.head.status !== 'PUBLISHED') {
      entry.head = a;
    }
  });
  return Array.from(byKey.values()).map(e => ({
    ...e.head,
    _hasPublished: e.hasPublished,
  }));
}

function ArticleList({
  articles,
  selectedKey,
  onSelect,
  onNew,
  searchQuery,
  onSearchChange,
}) {
  const grouped = useMemo(() => groupByArticleKey(articles || []), [articles]);
  const filtered = grouped.filter(article =>
    (article.title || '').toLowerCase().includes((searchQuery || '').toLowerCase()),
  );
  const liveCount = grouped.filter(a => a._hasPublished).length;

  return (
    <>
      <SearchInput
        placeholder="Search articles..."
        value={searchQuery || ''}
        onChange={e => onSearchChange(e.target.value)}
      />
      <NewButton label="+ New Article" onClick={onNew} />
      {KNOWLEDGE_BASE_BULK_UPLOAD_URL ? (
        <BulkUploadLink
          href={KNOWLEDGE_BASE_BULK_UPLOAD_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Bulk upload &#8599;
        </BulkUploadLink>
      ) : (
        <BulkUploadDisabled title="Bulk upload is only available from a deployed admin">
          Bulk upload &#8599;
        </BulkUploadDisabled>
      )}
      {grouped.length > 0 && (
        <ListSummary>
          {grouped.length} article{grouped.length === 1 ? '' : 's'} · {liveCount} live
        </ListSummary>
      )}
      <ListContainer>
        {filtered.map(article => (
          <ArticleItem
            key={article.article_key}
            $selected={selectedKey === article.article_key}
            onClick={() => onSelect(article)}
          >
            <LiveDot live={article._hasPublished} />
            <ArticleTitle>{article.title || 'Untitled'}</ArticleTitle>
          </ArticleItem>
        ))}
      </ListContainer>
    </>
  );
}

export default ArticleList;
